import React, {
  ChangeEvent,
  FC,
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { isEmpty } from 'lodash';
import Image from 'next/image';

import useFallbackTranslation from '../../utils/hooks/useFallbackTranslation';
import { Button } from '../Button/Button';
import { JabraDialog } from '../JabraDialog';
import { validateEngravingMessage } from './engravingMessageValidation';

export interface EngravingModalProps {
  engravingText?: string;
  renderingUuid?: string;
  productName?: string;
  previewImage?: string;
  onChange: (text?: string) => void;
  children: ReactElement;
}

const DEFAULT_ENGRAVING_TEXT = 'ABC';

export const EngravingModal: FC<EngravingModalProps> = ({
  engravingText,
  renderingUuid,
  productName,
  previewImage,
  onChange,
  children: modalTrigger,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const { t } = useFallbackTranslation();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [internalEngravingText, setInternalEngravingText] = useState<string | undefined>(
    engravingText
  );
  const handleOnClose = () => {
    setInternalEngravingText(engravingText);
    setIsOpen(false);
  };
  useEffect(() => {
    setInternalEngravingText(engravingText);
  }, [engravingText]);

  const isValid = internalEngravingText ? validateEngravingMessage(internalEngravingText) : true;
  const isTooLong = internalEngravingText ? internalEngravingText.length > 22 : false;

  const updateEngravingPreview = useCallback(
    (engravingText: string = DEFAULT_ENGRAVING_TEXT): void => {
      if (!validateEngravingMessage(engravingText)) {
        engravingText = DEFAULT_ENGRAVING_TEXT;
      }
      const iframe = iframeRef.current;
      if (!iframe) return;
      iframe.contentWindow?.postMessage(
        {
          uuid: renderingUuid,
          engraving: engravingText,
        },
        '*'
      );
    },
    [renderingUuid]
  );

  useEffect(() => {
    updateEngravingPreview(internalEngravingText);
  }, [internalEngravingText, updateEngravingPreview]);

  const handleIframeLoad = () => {
    updateEngravingPreview(internalEngravingText);
  };

  const handleSaveEngraving = () => {
    if (!isValid || isTooLong) {
      return;
    }
    if (isEmpty(internalEngravingText)) {
      handleRemoveEngraving();
      return;
    }
    onChange(internalEngravingText);
    setIsOpen(false);
  };

  const handleChangeEngraving = (event: ChangeEvent<HTMLInputElement>) => {
    setInternalEngravingText(event.target.value);
  };

  const handleRemoveEngraving = () => {
    onChange(undefined);
    setIsOpen(false);
  };

  return (
    <>
      {React.cloneElement(modalTrigger, { onClick: () => setIsOpen(true) })}
      <JabraDialog onClose={handleOnClose} open={isOpen} title={t('cart.EngravingPopupIntro')}>
        <div className="flex flex-col items-center bg-gray-50 p-8">
          {renderingUuid ? (
            <iframe
              tabIndex={-1}
              className="h-[25rem] max-h-[38vh] w-full"
              ref={iframeRef}
              loading="lazy"
              onLoad={handleIframeLoad}
              src="https://jabra.vntana.com/v3/src/index.html"
              title={productName}
            ></iframe>
          ) : (
            <>
              {previewImage && (
                <Image
                  height={400}
                  width={400}
                  className="h-[25rem] object-contain"
                  alt={productName ?? previewImage}
                  src={previewImage}
                />
              )}
            </>
          )}

          <p
            className="text-center text-xs leading-7 text-gray"
            dangerouslySetInnerHTML={{ __html: t('cart.EngravingPopupDisclaimer') }}
          ></p>
        </div>

        <div className="flex flex-col items-center justify-center">
          <form onSubmit={handleSaveEngraving} className="flex w-full flex-col gap-4 p-20">
            <input
              value={internalEngravingText}
              className="w-full rounded border p-3 px-5"
              type="text"
              onChange={handleChangeEngraving}
              placeholder={t('cart.EngravingInputPlaceholder')}
            />
            {!isValid && <p className="text-[red]"> {t('cart.EngravingMessageInValid')}</p>}
            {isTooLong && <p className="text-[red]"> {t('cart.EngravingMessageInValidLength')}</p>}
          </form>

          <div className="mb-8 flex gap-6">
            <Button
              variant="primary"
              size="s"
              disabled={!isValid || isTooLong}
              onClick={handleSaveEngraving}
            >
              {t('cart.EngravingButtonSave')}
            </Button>
            <Button variant="secondary" size="s" onClick={handleRemoveEngraving}>
              {t('cart.EngravingButtonRemove')}
            </Button>
          </div>
        </div>
      </JabraDialog>
    </>
  );
};
